import React, { useEffect, useState } from "react";
import { Save, Trash2, AlertTriangle, Copy } from "lucide-react";
import PageHeader from "@/components/admin/PageHeader";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";

export default function SettingsGeneral() {
    const [data, setData] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        adminApi.get("/api/admin/settings/general").then((r) => setData(r.data));
    }, []);

    if (!data) return <div className="p-10 text-stone-500">Loading…</div>;

    const save = async () => {
        setSaving(true);
        try {
            await adminApi.put("/api/admin/settings/general", {
                agent_name: data.agent_name,
                credits_limit_enabled: data.credits_limit_enabled,
                credits_limit: data.credits_limit,
            });
            toast.success("Settings saved");
        } finally {
            setSaving(false);
        }
    };

    const copyId = () => {
        navigator.clipboard?.writeText(data.agent_id);
        toast.success("Agent ID copied");
    };

    const deleteConversations = async () => {
        if (!window.confirm("Delete ALL conversations? This cannot be undone.")) return;
        await adminApi.post("/api/admin/settings/general/delete-conversations");
        toast.success("All conversations deleted");
    };

    return (
        <div className="p-10 max-w-4xl">
            <PageHeader eyebrow="Settings" title="General" testid="page-settings-general" />

            <div className="bg-white border border-stone-200 rounded-lg p-6 mb-6">
                <h2 className="font-heading text-xl text-orchid-700 mb-1">Agent details</h2>
                <p className="text-xs text-stone-500 mb-5">Configure Priya's identity and size.</p>

                <div className="space-y-5">
                    <Field label="Agent ID">
                        <div className="flex items-center gap-2">
                            <input
                                value={data.agent_id}
                                readOnly
                                data-testid="agent-id"
                                className="flex-1 px-3 py-2 rounded-md border border-stone-200 text-sm bg-stone-50 text-stone-600 font-mono"
                            />
                            <button
                                onClick={copyId}
                                className="p-2 rounded-md border border-stone-200 hover:bg-stone-50"
                                data-testid="agent-id-copy"
                            >
                                <Copy size={14} />
                            </button>
                        </div>
                    </Field>

                    <Field label="Size">
                        <input
                            value={`${data.size_mb} MB`}
                            readOnly
                            className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm bg-stone-50 text-stone-600"
                        />
                    </Field>

                    <Field label="Name">
                        <input
                            value={data.agent_name}
                            onChange={(e) => setData({ ...data, agent_name: e.target.value })}
                            data-testid="agent-name"
                            className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700 focus:ring-2 focus:ring-orchid-700/15"
                        />
                    </Field>
                </div>

                <button
                    onClick={save}
                    disabled={saving}
                    data-testid="general-save"
                    className="mt-6 inline-flex items-center gap-2 bg-orchid-700 hover:bg-orchid-600 text-white text-sm font-medium px-4 py-2 rounded-md disabled:opacity-60"
                >
                    <Save size={14} /> {saving ? "Saving…" : "Save"}
                </button>
            </div>

            <div className="bg-white border border-stone-200 rounded-lg p-6 mb-6">
                <h2 className="font-heading text-xl text-orchid-700 mb-1">Credits limit</h2>
                <p className="text-xs text-stone-500 mb-5">Cap the monthly LLM credits this agent can use.</p>

                <div className="flex items-center gap-3 mb-5">
                    <button
                        onClick={() =>
                            setData({ ...data, credits_limit_enabled: !data.credits_limit_enabled })
                        }
                        data-testid="credits-limit-toggle"
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            data.credits_limit_enabled ? "bg-orchid-700" : "bg-stone-300"
                        }`}
                    >
                        <span
                            className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                                data.credits_limit_enabled ? "translate-x-5" : "translate-x-0.5"
                            }`}
                        />
                    </button>
                    <span className="text-sm text-stone-700">Set credits limit on agent</span>
                </div>

                {data.credits_limit_enabled && (
                    <Field label="Monthly credits limit">
                        <input
                            type="number"
                            value={data.credits_limit || ""}
                            onChange={(e) => setData({ ...data, credits_limit: parseInt(e.target.value) || 0 })}
                            data-testid="credits-limit-value"
                            className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700"
                        />
                    </Field>
                )}
            </div>

            <div className="bg-white border-2 border-red-200 rounded-lg p-6" data-testid="danger-zone">
                <div className="flex items-center gap-2 mb-4">
                    <AlertTriangle size={18} className="text-red-700" />
                    <h2 className="font-heading text-xl text-red-700">Danger zone</h2>
                </div>

                <div className="space-y-4">
                    <div className="flex items-start justify-between gap-4 pb-4 border-b border-red-100">
                        <div>
                            <div className="font-medium text-stone-900 text-sm">Delete all conversations</div>
                            <div className="text-xs text-stone-600 mt-1">Permanently removes every chat log and visitor session.</div>
                        </div>
                        <button
                            onClick={deleteConversations}
                            data-testid="delete-conversations"
                            className="inline-flex items-center gap-1.5 text-red-700 border border-red-300 hover:bg-red-50 text-xs font-medium px-3 py-2 rounded-md"
                        >
                            <Trash2 size={13} /> Delete
                        </button>
                    </div>

                    <div className="flex items-start justify-between gap-4">
                        <div>
                            <div className="font-medium text-stone-900 text-sm">Delete agent</div>
                            <div className="text-xs text-stone-600 mt-1">Permanently deletes Priya, data sources, settings and history.</div>
                        </div>
                        <button
                            disabled
                            className="inline-flex items-center gap-1.5 text-red-300 border border-red-200 text-xs font-medium px-3 py-2 rounded-md cursor-not-allowed"
                            title="Contact support"
                        >
                            <Trash2 size={13} /> Delete
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

const Field = ({ label, children }) => (
    <div>
        <label className="text-[11px] uppercase tracking-[0.2em] text-stone-500 mb-2 block">{label}</label>
        {children}
    </div>
);
